import React, { useState } from "react";
import { SelectChangeEvent } from "@mui/material";
import { useMutation, useQuery } from "react-query";
import {
  getServiceRepairs,
  postRepairStatus,
} from "../../../../api/ServiceApi";
import useSessionState from "../../../../hooks/useSessionState";

function useTableSelector() {
  const [pageService, setPage] = useSessionState("pageService", 0);
  const [rowsPerPageService, setRowsPerPage] = useSessionState(
    "rowsPerPageService",
    15
  );

  const [isServiceSearch, setSearch] = useSessionState(
    "isServiceSearch",
    false
  );

  const [filterByService, setFilterBy] = useSessionState(
    "filterByService",
    "snNumber"
  );

  const [selected, setSelected] = useState<string[]>([]);

  const [searchTermService, setSearchTerm] = useSessionState(
    "searchTermService",
    ""
  );

  const { error, isFetching, data } = useQuery(
    [[isServiceSearch, "ServiceRepairs"], pageService, rowsPerPageService],
    async () => {
      if (
        searchTermService &&
        filterByService === "snNumber" &&
        isNaN(parseInt(searchTermService))
      )
        return "Must be Number";

      const response = await getServiceRepairs({
        pageNumber: pageService,
        rowsPerPage: rowsPerPageService,
        searchTerm: searchTermService,
        searchBy: filterByService
      });

      return response?.data;
    },
    { refetchOnWindowFocus: false, retry: 2 }
  );

  const role = JSON.parse(sessionStorage.getItem("role") || JSON.stringify(""));

  const hasAdminAccess = role === "Admin" || role === "Inside Sales";

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    const term = value.toLowerCase();
    setSearchTerm(term);
  };

  const handleFilterByChange = (event: SelectChangeEvent<string>) =>
    setFilterBy(event.target.value);

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key !== "Enter") return;
    setPage(0);
    setSearch(!isServiceSearch);
  };

  const handleSearchBtn = () => {
    setPage(0);
    setSearch((prev: boolean) => !prev);
  };

  const handleReset = () => {
    setSearchTerm("");
    setPage(0);
    setSearch(!isServiceSearch);
  };

  const mutation = useMutation(
    async (status: string) => {
      if (!status) return;
      return await postRepairStatus(selected, status);
    },
    {
      onSuccess: async () => {
        setSelected([]);
        handleCloseModal();
        setSearch(!isServiceSearch);
      },
      onError: (err) => console.log(err),
    }
  );

  const handleChangePage = (
    event: React.MouseEvent<any> | null,
    newPage: number
  ) => setPage(newPage);

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleCheck = (
    event: React.ChangeEvent<HTMLInputElement>,
    id: string
  ) => {
    if (!hasAdminAccess) {
      return null;
    }
    const selectedIndex = selected.indexOf(id);
    let newSelected: string[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    setSelected(newSelected);
  };

  const unCheckAll = () => setSelected([]);

  const [hoverId, setHoverId] = useState("");

  const [openModal, setOpenModal] = useState(false);

  const mouseEnter = (unitId: string) => {
    setHoverId(unitId);
  };

  const mouseLeave = () => {
    if (!selected.length) {
      setHoverId("");
    }
  };

  const handleCloseModal = () => setOpenModal(false);
  const handleOpenModal = () => setOpenModal(true);

  const [openApt, setOpenApt] = useState(false);

  const [aptId, setAptId] = useState("");

  const handleCloseApt = () => {
    setOpenApt(false);
    setAptId("");
  };

  const handleOpenApt = () => {
    setOpenApt(true);
    setAptId(selected[0] || "");
  };

  const isSelected = (id: string) => selected.indexOf(id) !== -1;

  return {
    pageService,
    rowsPerPageService,
    isServiceSearch,
    filterByService,
    selected,
    searchTermService,
    error,
    isFetching,
    data,
    role,
    hasAdminAccess,
    handleChange,
    handleFilterByChange,
    onKeyDown,
    handleSearchBtn,
    handleReset,
    mutation,
    handleChangePage,
    handleChangeRowsPerPage,
    handleCheck,
    unCheckAll,
    hoverId,
    openModal,
    mouseEnter,
    mouseLeave,
    handleCloseModal,
    handleOpenModal,
    openApt,
    aptId,
    handleCloseApt,
    handleOpenApt,
    isSelected,
    setSearch
  };
}

export default useTableSelector;
